/**
 * bc-reviews-slider — Review cards carousel with an infinite loop.
 *
 * The loop is done by cloning the whole set of cards before and after the
 * originals and starting scrolled to the middle copy. Once scrolling settles
 * past a boundary, scrollLeft jumps by exactly one set width with smooth
 * scrolling disabled, so the seam is never visible.
 */
(function () {
  'use strict';

  var SETTLE_MS = 90;

  document.addEventListener('DOMContentLoaded', initAll);

  /* Theme editor: re-init when a section is added or its blocks change */
  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-bc-reviews]').forEach(initSlider);
  });

  function initAll() {
    document.querySelectorAll('[data-bc-reviews]').forEach(initSlider);
  }

  function initSlider(root) {
    if (root.dataset.bcReviewsReady === 'true') return;

    var track = root.querySelector('[data-bc-reviews-track]');
    if (!track) return;

    var originals = Array.prototype.slice.call(track.children);
    var count = originals.length;
    if (!count) return;

    root.dataset.bcReviewsReady = 'true';

    var dotsWrap = root.querySelector('[data-bc-reviews-dots]');
    var prevBtn = root.querySelector('[data-bc-reviews-arrow="prev"]');
    var nextBtn = root.querySelector('[data-bc-reviews-arrow="next"]');

    var looping = false;
    var setWidth = 0;
    var current = 0;
    var settleTimer;
    var rafPending = false;
    var lastStep = 0;
    var resizeObserver = null;

    buildDots();
    bindControls();

    /* The stylesheet loads async, so at DOMContentLoaded the cards may still be
       unstyled. Measure now, again on load once the CSS has applied, and after
       that on any resize. ResizeObserver alone is not enough: its callbacks are
       tied to the rendering steps, so a backgrounded tab would never init. */
    refresh();
    window.addEventListener('load', refresh);

    if (typeof ResizeObserver === 'function') {
      resizeObserver = new ResizeObserver(debounce(refresh, 150));
      resizeObserver.observe(track);
    } else {
      window.addEventListener('resize', debounce(refresh, 200));
    }

    /**
     * Rebuild when the card pitch changes — that covers the async stylesheet
     * landing and every breakpoint crossing. Keyed on the step rather than the
     * track width, because applying the CSS resizes the cards while leaving the
     * track the same width. Cloning does not affect the step, so no recursion.
     */
    function refresh() {
      var step = cardStep();
      if (!step) return;
      if (Math.abs(step - lastStep) < 1) return;
      lastStep = step;
      teardownLoop();
      setupLoop();
    }

    /* ── Infinite loop setup ──────────────────────────────────────────── */

    /**
     * Cloning only pays off when the cards overflow the track. With a set
     * that already fits, a loop would jump against nothing to scroll.
     */
    function setupLoop() {
      setWidth = measureSetWidth();
      looping = setWidth > track.clientWidth + 1;

      if (!looping) {
        updateDots(0);
        return;
      }

      var before = document.createDocumentFragment();
      var after = document.createDocumentFragment();

      originals.forEach(function (card) {
        before.appendChild(makeClone(card));
        after.appendChild(makeClone(card));
      });

      track.insertBefore(before, track.firstChild);
      track.appendChild(after);

      jumpTo(setWidth);
      updateDots(0);
    }

    function teardownLoop() {
      track.querySelectorAll('[data-bc-reviews-clone]').forEach(function (n) {
        n.remove();
      });
    }

    function makeClone(card) {
      var clone = card.cloneNode(true);
      clone.setAttribute('data-bc-reviews-clone', '');
      clone.setAttribute('aria-hidden', 'true');
      /* Keep clones out of the tab order and off the a11y tree */
      clone.querySelectorAll('a, button, [tabindex]').forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
      return clone;
    }

    /* ── Geometry ─────────────────────────────────────────────────────── */

    function cardStep() {
      var card = track.querySelector('.bc-review-card');
      if (!card) return 0;
      var gap = parseFloat(getComputedStyle(track).columnGap);
      if (isNaN(gap)) gap = 18;
      return card.getBoundingClientRect().width + gap;
    }

    function measureSetWidth() {
      return cardStep() * count;
    }

    /* ── Scrolling ────────────────────────────────────────────────────── */

    function jumpTo(left) {
      track.classList.add('is-jumping');
      track.scrollLeft = left;
      /* Force a reflow so the class removal cannot be batched with the jump */
      void track.offsetWidth;
      track.classList.remove('is-jumping');
    }

    function scrollByCards(delta) {
      track.scrollBy({ left: cardStep() * delta, behavior: behavior() });
    }

    function behavior() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth';
    }

    /**
     * Originals occupy [setWidth, 2 × setWidth). Drifting more than half a set
     * past either edge means we can rewind by a whole set and land on the
     * identical card, which is what makes the loop seamless.
     */
    function wrapIfNeeded() {
      if (!looping) return;
      var x = track.scrollLeft;
      if (x < setWidth * 0.5) {
        jumpTo(x + setWidth);
      } else if (x > setWidth * 1.5) {
        jumpTo(x - setWidth);
      }
    }

    track.addEventListener('scroll', function () {
      if (!rafPending) {
        rafPending = true;
        requestAnimationFrame(function () {
          rafPending = false;
          updateDots(activeIndex());
        });
      }

      clearTimeout(settleTimer);
      settleTimer = setTimeout(wrapIfNeeded, SETTLE_MS);
    }, { passive: true });

    function activeIndex() {
      var step = cardStep();
      if (!step) return 0;
      var offset = looping ? track.scrollLeft - setWidth : track.scrollLeft;
      var i = Math.round(offset / step) % count;
      if (i < 0) i += count;
      return i;
    }

    /* ── Controls ─────────────────────────────────────────────────────── */

    function bindControls() {
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          scrollByCards(-1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          scrollByCards(1);
        });
      }
    }

    function buildDots() {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';

      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'bc-reviews-slider__dot' + (i === 0 ? ' is-active' : '');
        dot.setAttribute('data-index', i);
        dot.setAttribute('aria-label', 'Go to review ' + (i + 1));
        dot.addEventListener('click', function () {
          goTo(parseInt(this.getAttribute('data-index'), 10));
        });
        dotsWrap.appendChild(dot);
      }
    }

    function goTo(index) {
      var base = looping ? setWidth : 0;
      track.scrollTo({ left: base + index * cardStep(), behavior: behavior() });
      updateDots(index);
    }

    function updateDots(index) {
      if (!dotsWrap || index === current) return;
      current = index;
      var dots = dotsWrap.querySelectorAll('.bc-reviews-slider__dot');
      Array.prototype.forEach.call(dots, function (d, i) {
        d.classList.toggle('is-active', i === index);
      });
    }
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }
})();
